<template>
    <div>
        <app-template>
            <div class="flex items-center justify-between mb-6">
                <h1 class="text-xl font-bold text-gray-800">
                    <i class="fas fa-search text-blue-600 mr-2"></i>
                    Détecteurs de fuites
                </h1>

                <button class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center" @click="openModal()">
                    <i class="fas fa-plus mr-2"></i>
                    Ajouter
                </button>
            </div>

            <!-- TABLE -->
            <div v-if="loadingGetData" class="p-6 text-center">
                <i class="fas fa-spinner fa-spin text-gray-500 text-2xl"></i>
            </div>
            <div v-if="!loadingGetData">
                <div class="bg-white rounded shadow overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <thead class="bg-gray-100 text-gray-700">
                            <tr>
                                <th class="p-3 text-left">Alerte</th>
                                <th class="p-3 text-left">Marque</th>
                                <th class="p-3 text-left">Numéro de série</th>
                                <th class="p-3 text-left">Assigné à</th>
                                <th class="p-3 text-left">Étalonnage</th>
                                <th class="p-3 text-left">Fin validité</th>
                                <th class="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="detecteur in detecteurs" :key="detecteur.id" class="border-t hover:bg-red-50 cursor-pointer" @click="openModal(detecteur)">
                                <td class="p-3 font-medium">
                                    <span v-html="alertMessage(detecteur)"></span>
                                </td>
                                <td class="p-3 font-medium">{{ detecteur.marque }}</td>
                                <td class="p-3 font-medium">{{ detecteur.num_serie }}</td>
                                <td class="p-3">
                                    {{ getTechName(detecteur.tech_id) || '-' }}
                                </td>
                                <td class="p-3">
                                    {{ detecteur.date_etalonage | momentDate }}
                                </td>
                                <td class="p-3">
                                    <span :class="isExpired(detecteur.date_fin_validite) ? 'text-red-600 font-semibold' : ''">
                                        {{ detecteur.date_fin_validite | momentDate }}
                                    </span>
                                </td>
                                <td class="p-3 text-right space-x-2">
                                    <button class="text-red-600 hover:text-red-800" @click.stop="remove(detecteur)">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>

                            <tr v-if="!detecteurs.length">
                                <td colspan="5" class="p-6 text-center text-gray-500">
                                    Aucun détecteur enregistré
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- MODAL -->
            <modal name="detecteur-modal" height="auto" :adaptive="true">
                <div class="p-6 overflow-y-auto max-h-[100vh]">
                    <h2 class="text-lg font-bold mb-4">
                        {{ form.id ? 'Modifier' : 'Ajouter' }} un détecteur
                    </h2>

                    <div class="space-y-4">
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium">Marque</label>
                                <input v-model="form.marque" class="w-full border rounded px-3 py-2" type="text" id="inp-marque" />
                            </div>
    
                            <div>
                                <label class="block text-sm font-medium">Numéro de série</label>
                                <input v-model="form.num_serie" class="w-full border rounded px-3 py-2" type="text" id="inp-num_serie" />
                            </div>
                        </div>

                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm font-medium">Date étalonnage</label>
                                <input @input="setDateEtalonage" v-model="form.date_etalonage" :max="maxDate" class="w-full border rounded px-3 py-2" type="date" id="inp-date-etalonage">
                            </div>

                            <div>
                                <label class="block text-sm font-medium">Fin de validité</label>
                                <input v-model="form.date_fin_validite" class="w-full border rounded px-3 py-2" type="date" id="inp-date-fin-validite">
                            </div>
                        </div>

                        <div>
                            <label class="block text-sm font-medium">Détecteur assigné à</label>
                            <select v-model="form.tech_id" class="w-full border rounded px-3 py-2">
                                <option :value="null">Aucun technicien</option>
                                <option v-for="tech in techniciens" :key="tech.id" :value="tech.id">
                                    {{ tech.prenom }} {{ tech.nom }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="flex justify-end mt-6 space-x-3">
                        <button class="px-4 py-2 rounded border" @click="$modal.hide('detecteur-modal')">
                            Annuler
                        </button>
                        <button :disabled="loadingSave" class="bg-blue-600 text-white px-4 py-2 rounded" @click="save">
                            <i v-if="loadingSave" class="fas fa-spinner fa-spin"></i>
                            <template v-if="!loadingSave">
                                Enregistrer
                            </template>
                        </button>
                    </div>
                </div>
            </modal>
        </app-template>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    data() {
        return {
            detecteurs: [],
            techniciens: [],
            form: {
                id: null,
                marque: '',
                num_serie: '',
                tech_id: null,
                date_etalonage: null,
                date_fin_validite: null,
            },

            loadingGetData: false,
            loadingSave: false,
        }
    },

    filters: {
        momentDate(value) {
            if (!value) {
                return '-';
            }
            return moment(value).format('DD/MM/YYYY');
        }
    },

    computed: {
        maxDate() {
            return moment().format('YYYY-MM-DD')
        }
    },

    methods: {
        async fetchDetecteurs() {
            this.loadingGetData = true
            let res = await this.$request.get('/detecteurs/list')
            this.detecteurs = res
            this.loadingGetData = false
        },

        async fetchTechniciens() {
            let res = await this.$request.get('/techniciens')
            this.techniciens = res
        },

        openModal(detecteur = null) {
            this.form = detecteur
                ? { ...detecteur }
                : {
                    id: null,
                    marque: '',
                    num_serie: '',
                    tech_id: null,
                    date_etalonage: null,
                    date_fin_validite: null,
                }
            this.form.date_etalonage = this.form.date_etalonage
                ? moment(this.form.date_etalonage).format('YYYY-MM-DD')
                : null
            this.form.date_fin_validite = this.form.date_fin_validite
                ? moment(this.form.date_fin_validite).format('YYYY-MM-DD')
                : null

            this.$modal.show('detecteur-modal')
        },

        async save() {
            const validation = this.$inpError([
                'inp-marque:required|min=2',
                'inp-num_serie:required|min=2',
                'inp-date-etalonage:required',
                'inp-date-fin-validite:required',
            ]);

            if (!validation) {
                this.$toast.error('Formulaire invalide')
                return
            }

            // La date de fin de validité doit être après la date d'étalonnage
            if (this.form.date_etalonage && this.form.date_fin_validite) {
                if (new Date(this.form.date_fin_validite) <= new Date(this.form.date_etalonage)) {
                    this.$toast.error("La date de fin de validité doit être après la date d'étalonnage.")
                    return
                }
            }
            const route = this.form.id ? '/detecteurs/update' : '/detecteurs/store'

            this.loadingSave = true
            let response = await this.$request.post(route, {
                ...this.form,
                societe_id: this.societeId
            })
            await this.fetchDetecteurs()
            this.$modal.hide('detecteur-modal')
            this.loadingSave = false
            this.$eventHub.$emit('refresh-pastilles', 'detecteurs')
        },

        remove(detecteur) {
            if (!confirm('Supprimer ce détecteur ?')) return

            this.$request.post('/detecteurs/delete', {
                id: detecteur.id
            }).then(() => {
                this.fetchDetecteurs()
            })
        },

        getTechName(id) {
            const tech = this.techniciens.find(t => t.id === id)
            return tech ? `${tech.prenom} ${tech.nom}` : null
        },

        isExpired(date) {
            if (!date) return false
            return new Date(date) < new Date()
        },

        setDateEtalonage() {
            if (!this.form.date_etalonage) return
            const etalonageDate = new Date(this.form.date_etalonage)
            const finValiditeDate = new Date(etalonageDate)
            finValiditeDate.setFullYear(finValiditeDate.getFullYear() + 1)
            this.form.date_fin_validite = moment(finValiditeDate).format('YYYY-MM-DD')
        },

        alertMessage(detecteur) {
            // Si la date de fin de validité est dans moins de 30 jours
            if (!detecteur.date_fin_validite) return ''
            const finValiditeDate = new Date(detecteur.date_fin_validite)
            const today = new Date()
            const timeDiff = finValiditeDate - today
            const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
            if (daysDiff < 30 && daysDiff >= 0) {
                return `<span class="bg-orange-200 text-orange-800 p-2"><i class="fas fa-exclamation-triangle"></i> Alerte : La date de fin de validité est dans ${daysDiff} jours.</span>`
            }
            if (this.isExpired(detecteur.date_fin_validite)) {
                return `<span class="bg-red-200 text-red-800 p-2"><i class="fas fa-exclamation-circle"></i> Alerte : La date de fin de validité est dépassée.</span>`
            }
            return '-'
        }
    },

    mounted() {
        this.fetchDetecteurs()
        this.fetchTechniciens()
    },
}
</script>
